// Everything you can do to one of your days.
//
// A dialog rather than controls inside the expanded day, because the panel is
// for reading a day and this is for changing one. Splitting those across two
// surfaces was how "different hours" ended up here while "turn the day off"
// stayed behind — one pencil, one place, is easier to find and harder to half
// use.
//
// It opens on whatever the day already does — the override if there is one,
// otherwise the recurring hours in effect — so nobody is asked to re-enter
// hours they already have.
//
// Time off is the second half of the same question. It used to be a card at the
// bottom of the page, which meant a day was managed in two unrelated places
// depending on whether you were adding hours or taking them away. It's here
// now, and it asks for a reason, because "off" on a calendar cell is the least
// useful thing a day can say — a barn wants to know whether Kate is at a show
// or at the dentist, and everyone reading the calendar wants it without asking.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import TimeSelect from '../ui/TimeSelect';
import { fmtDate } from '../../lib/dates';

const SLOT_LENGTHS = [30, 45, 60, 90];
const CHANGEOVERS = [0, 15, 30, 45];

// The four reasons a barn day actually goes away, in rough order of frequency.
// Chips rather than a dropdown: it's one tap for the common case, and the field
// underneath still takes anything, because the fifth reason always exists.
const REASONS = ['Show', 'Vacation', 'Sick', 'Appointment'];

function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Sep 12 — for naming a stretch without repeating the year four times. */
function fmtShort(iso) {
  return fmtDate(iso, 'short');
}

function fmtTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}


export default function DayHoursDialog({
  date,
  trainerUserId,
  saving = false,
  onClose,
  onSave,
  onClear,
  onMarkTimeOff,
  onClearTimeOff,
}) {
  const [hours, setHours] = useState(null);
  // A stretch, because taking a fortnight off one day at a time was fourteen
  // trips through this dialog.
  const [offThrough, setOffThrough] = useState('');
  const [reason, setReason] = useState('');
  const [allDay, setAllDay] = useState(true);
  const [offWindow, setOffWindow] = useState({ startTime: '12:00', endTime: '15:00' });

  const dayQ = useQuery({
    queryKey: ['availability-day', date, trainerUserId],
    queryFn: () => api.get(`/availability/day/${date}?trainerUserId=${trainerUserId}`),
    retry: false,
  });

  const day = dayQ.data;
  const entries = day?.timeOff ?? [];
  // An all-day entry is the day's whole state — there are no hours to discuss.
  // A part-day one sits alongside hours that still run, so it's listed rather
  // than taking the dialog over.
  const wholeDayOff = entries.find((e) => e.allDay) ?? null;
  const partials = entries.filter((e) => !e.allDay);

  useEffect(() => {
    if (day && !hours) {
      setHours({
        startTime: day.hours?.startTime ?? '15:00',
        endTime: day.hours?.endTime ?? '18:00',
        slotMinutes: day.hours?.slotMinutes ?? 45,
        changeoverMinutes: day.hours?.changeoverMinutes ?? 0,
        capacity: day.hours?.capacity ?? 1,
      });
      // Part of a day means part of the day you actually work, so the pickers
      // open inside those hours rather than on an arbitrary midday.
      if (day.hours) {
        setOffWindow({ startTime: day.hours.startTime, endTime: day.hours.endTime });
      }
    }
  }, [day, hours]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-xl p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-medium text-lg">{fmtDate(date, 'dayMedium')}</h2>
        <p className="text-sm text-ink-500 mt-1">
          {wholeDayOff
            ? `Marked off${wholeDayOff.reason ? ` — ${wholeDayOff.reason}` : ''}.`
            : 'Modify slots for this day only.'}
        </p>

        {dayQ.isLoading || !hours ? (
          <div className="h-32 mt-4 bg-ink-50 rounded animate-pulse" />
        ) : wholeDayOff ? (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {/* A day inside a stretch gets both verbs, and the narrower one
                leads: you opened one date, so that's the one you most likely
                mean, and it's the choice you can undo in a tap. Clearing a day
                from the middle splits the stretch either side of it — the
                server does that rather than making you delete and retype the
                half you're keeping. */}
            <button
              type="button"
              className="btn-primary"
              disabled={saving}
              onClick={() => onClearTimeOff(wholeDayOff, { day: date })}
            >
              {wholeDayOff.spansMultipleDays ? 'Clear Just This Day' : 'Clear This Time Off'}
            </button>
            {wholeDayOff.spansMultipleDays && (
              <button
                type="button"
                className="btn-secondary"
                disabled={saving}
                onClick={() => onClearTimeOff(wholeDayOff)}
              >
                Clear All {fmtShort(wholeDayOff.startDate)}–{fmtShort(wholeDayOff.endDate)}
              </button>
            )}
            <button type="button" className="btn-secondary" disabled={saving} onClick={onClose}>
              Close
            </button>
            {wholeDayOff.spansMultipleDays && (
              <p className="text-xs text-ink-500 w-full">
                This day is part of {fmtShort(wholeDayOff.startDate)}–
                {fmtShort(wholeDayOff.endDate)}.
              </p>
            )}
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">From</label>
                <TimeSelect
                  label="From"
                  value={hours.startTime}
                  onChange={(v) => setHours({ ...hours, startTime: v })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">To</label>
                <TimeSelect
                  label="To"
                  value={hours.endTime}
                  onChange={(v) => setHours({ ...hours, endTime: v })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="dh-len">
                  Lesson length
                </label>
                <select
                  id="dh-len"
                  className="input"
                  value={hours.slotMinutes}
                  onChange={(e) => setHours({ ...hours, slotMinutes: Number(e.target.value) })}
                >
                  {SLOT_LENGTHS.map((m) => (
                    <option key={m} value={m}>{m} min</option>
                  ))}
                  {!SLOT_LENGTHS.includes(hours.slotMinutes) && (
                    <option value={hours.slotMinutes}>{hours.slotMinutes} min</option>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="dh-gap">
                  Changeover
                </label>
                <select
                  id="dh-gap"
                  className="input"
                  value={hours.changeoverMinutes ?? 0}
                  onChange={(e) => setHours({ ...hours, changeoverMinutes: Number(e.target.value) })}
                >
                  {CHANGEOVERS.map((m) => (
                    <option key={m} value={m}>{m === 0 ? 'None' : `${m} min`}</option>
                  ))}
                  {!CHANGEOVERS.includes(hours.changeoverMinutes ?? 0) && (
                    <option value={hours.changeoverMinutes}>{hours.changeoverMinutes} min</option>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="dh-cap">
                  Riders per slot
                </label>
                <select
                  id="dh-cap"
                  className="input"
                  value={hours.capacity}
                  onChange={(e) => setHours({ ...hours, capacity: Number(e.target.value) })}
                >
                  {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                    <option key={n} value={n}>{n === 1 ? '1 (private)' : n}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button
                type="button"
                className="btn-primary"
                disabled={saving}
                onClick={() => onSave(hours)}
              >
                {saving ? 'Saving…' : 'Use These Hours'}
              </button>
              <button type="button" className="btn-secondary" disabled={saving} onClick={onClose}>
                Cancel
              </button>
              {day?.overridden && (
                <button
                  type="button"
                  className="text-sm text-ink-500 hover:text-ink-800 ml-auto"
                  disabled={saving}
                  onClick={onClear}
                >
                  Back to usual hours
                </button>
              )}
            </div>

            {/* Separated by a rule and left to last: this is the one thing here
                that takes the day away rather than adjusting it. */}
            <div className="pt-3 border-t border-ink-100 space-y-2.5">
              <h3 className="text-sm font-medium text-ink-700">Mark time off</h3>

              {partials.length > 0 && (
                <ul className="space-y-1">
                  {partials.map((e) => (
                    <li
                      key={e.id}
                      className="flex items-center gap-2 text-xs rounded-md bg-ink-50 px-2 py-1.5"
                    >
                      <span className="font-medium text-ink-700">{e.reason || 'Time off'}</span>
                      <span className="text-ink-500 tabular-nums">
                        {fmtTime(e.startTime)} – {fmtTime(e.endTime)}
                      </span>
                      {e.spansMultipleDays && (
                        <span className="text-ink-400">
                          {fmtShort(e.startDate)}–{fmtShort(e.endDate)}
                        </span>
                      )}
                      <button
                        type="button"
                        className="ml-auto text-ink-400 hover:text-rose-700 text-base leading-none px-1"
                        aria-label="Clear this time off"
                        disabled={saving}
                        onClick={() => onClearTimeOff(e)}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* The reason is the point of the whole exercise, so it comes
                  first and it is one tap for the four that cover most of it.
                  The field underneath is still free text — a chip that fills a
                  box you can then edit beats a dropdown you have to escape. */}
              <div className="flex flex-wrap items-center gap-1.5">
                {REASONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setReason(reason === r ? '' : r)}
                    className={
                      'px-2.5 py-1 rounded-full text-xs border transition-colors ' +
                      (reason === r
                        ? 'bg-hunter text-white border-hunter'
                        : 'bg-white text-ink-600 border-ink-200 hover:border-ink-400')
                    }
                  >
                    {r}
                  </button>
                ))}
                <input
                  type="text"
                  className="input py-1 text-xs flex-1 min-w-[8rem]"
                  placeholder="or type a reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="dh-span"
                    checked={allDay}
                    onChange={() => setAllDay(true)}
                  />
                  All day
                </label>
                <label className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name="dh-span"
                    checked={!allDay}
                    onChange={() => setAllDay(false)}
                  />
                  Part of the day
                </label>
              </div>

              {/* Only the slots inside this window go; the rest of the day runs
                  as usual. The engine has always worked this way — nothing
                  exposed it, so a dentist appointment cost a whole afternoon. */}
              {!allDay && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 pt-1">
                  <div>
                    <label className="block text-xs font-medium text-ink-600 mb-1">Away from</label>
                    <TimeSelect
                      label="Away from"
                      value={offWindow.startTime}
                      onChange={(v) => setOffWindow({ ...offWindow, startTime: v })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-600 mb-1">Back at</label>
                    <TimeSelect
                      label="Back at"
                      value={offWindow.endTime}
                      onChange={(v) => setOffWindow({ ...offWindow, endTime: v })}
                    />
                  </div>
                </div>
              )}

              <label className="flex items-center gap-2 text-xs text-ink-500">
                <span className="shrink-0">Through</span>
                <input
                  type="date"
                  className="input py-1 text-xs w-auto"
                  min={addDays(date, 1)}
                  value={offThrough}
                  onChange={(e) => setOffThrough(e.target.value)}
                />
                {offThrough ? (
                  <button
                    type="button"
                    className="text-ink-400 hover:text-ink-700"
                    onClick={() => setOffThrough('')}
                  >
                    clear
                  </button>
                ) : (
                  <span className="text-ink-400">for a longer stretch</span>
                )}
              </label>

              <div className="flex flex-wrap items-center gap-2 pt-0.5">
                <button
                  type="button"
                  className="btn-secondary text-rose-700 border-rose-200 hover:border-rose-400"
                  disabled={saving || (!allDay && offWindow.endTime <= offWindow.startTime)}
                  onClick={() =>
                    onMarkTimeOff({
                      through: offThrough && offThrough > date ? offThrough : date,
                      reason: reason.trim(),
                      startTime: allDay ? null : offWindow.startTime,
                      endTime: allDay ? null : offWindow.endTime,
                    })
                  }
                >
                  Mark Time Off
                </button>
                <p className="text-xs text-ink-400 flex-1 min-w-[12rem]">
                  Stops new bookings. Anything already booked stays unless you say otherwise.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
